/**
 * ProfileModal.tsx
 * Lets any logged-in officer view and edit their own profile:
 * - Display name, email, phone
 * - Change password
 * Available from TopBar (desktop/Electron) and MobileCommandView.
 */
import { useState, useEffect } from 'react'
import { useAuthStore } from '../../../stores/auth'
import { workerGetV1, workerPutV1, sha256 } from '../../../api/worker'

interface Props {
  onClose: () => void
}

interface UserProfile {
  call_sign: string
  name: string
  rank: string
  employee_no: string
  email: string
  phone: string
  status: string
}

type Tab = 'profile' | 'password'

export default function ProfileModal({ onClose }: Props) {
  const session = useAuthStore((s: { session: any }) => s.session)

  const [tab, setTab]           = useState<Tab>('profile')
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [msg, setMsg]           = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  // Profile fields
  const [profile, setProfile]   = useState<UserProfile | null>(null)
  const [name, setName]         = useState('')
  const [email, setEmail]       = useState('')
  const [phone, setPhone]       = useState('')

  // Password fields
  const [currentPw, setCurrentPw]   = useState('')
  const [newPw, setNewPw]           = useState('')
  const [confirmPw, setConfirmPw]   = useState('')

  useEffect(() => {
    async function load() {
      try {
        // API returns flat shape: { success, callSign, name, rank, email, phone, employeeNo }
        const res = await workerGetV1<{
          success: boolean
          callSign?: string
          name?: string
          rank?: string
          employee_no?: string
          employeeNo?: string
          email?: string
          phone?: string
          status?: string
        }>('/api/v1/profile')
        if (res.success) {
          const p: UserProfile = {
            call_sign:   res.callSign   ?? session?.callSign ?? '',
            name:        res.name       ?? session?.name     ?? '',
            rank:        res.rank       ?? session?.rank     ?? '',
            employee_no: res.employee_no ?? res.employeeNo  ?? '',
            email:       res.email      ?? '',
            phone:       res.phone      ?? '',
            status:      res.status     ?? 'approved',
          }
          setProfile(p)
          setName(p.name)
          setEmail(p.email)
          setPhone(p.phone)
        }
      } catch { /* silent */ }
      finally { setLoading(false) }
    }
    load()
  }, [])

  async function saveProfile() {
    if (!name.trim()) { setMsg({ type: 'err', text: 'Name is required' }); return }
    setSaving(true); setMsg(null)
    try {
      const res = await workerPutV1<{ success: boolean; message?: string; error?: string }>(
        '/api/v1/profile', { name: name.trim(), email: email.trim(), phone: phone.trim() }
      )
      if (res.success) setMsg({ type: 'ok', text: 'Profile updated successfully.' })
      else setMsg({ type: 'err', text: res.error || 'Update failed' })
    } catch { setMsg({ type: 'err', text: 'Network error. Try again.' }) }
    finally { setSaving(false) }
  }

  async function changePassword() {
    if (!currentPw)           { setMsg({ type: 'err', text: 'Enter your current password' }); return }
    if (newPw.length < 8)     { setMsg({ type: 'err', text: 'New password must be at least 8 characters' }); return }
    if (newPw !== confirmPw)  { setMsg({ type: 'err', text: 'New passwords do not match' }); return }
    setSaving(true); setMsg(null)
    try {
      const currentHash = await sha256(currentPw)
      const newHash     = await sha256(newPw)
      const res = await workerPutV1<{ success: boolean; message?: string; error?: string }>(
        '/api/v1/profile/password', { currentPasswordHash: currentHash, newPasswordHash: newHash }
      )
      if (res.success) {
        setMsg({ type: 'ok', text: 'Password changed. Use your new password next time you sign in.' })
        setCurrentPw(''); setNewPw(''); setConfirmPw('')
      } else {
        setMsg({ type: 'err', text: res.error || 'Password change failed' })
      }
    } catch { setMsg({ type: 'err', text: 'Network error. Try again.' }) }
    finally { setSaving(false) }
  }

  const inp: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box' as const,
    background: 'var(--bg-elevated, rgba(255,255,255,0.06))',
    border: '1px solid var(--border, rgba(255,255,255,0.12))',
    borderRadius: 6, padding: '8px 10px',
    color: 'var(--text-primary, #f1f5f9)',
    fontSize: 13, outline: 'none',
  }
  const lbl: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: 'var(--text-muted, rgba(255,255,255,0.45))',
    textTransform: 'uppercase' as const,
    letterSpacing: '.07em', marginBottom: 5,
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--bg-surface, #0f1c2e)', border: '1px solid var(--border, rgba(255,255,255,0.1))', borderRadius: 12, width: '100%', maxWidth: 420, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: '50%',
              background: 'var(--accent, #c9a84c)', color: '#1a2744',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 15, flexShrink: 0,
            }}>
              {(session?.callSign ?? '?').slice(-2)}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary, #f1f5f9)' }}>
                {session?.name ? session.name.split(',')[0].trim() : session?.callSign}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{session?.callSign} · {session?.rank}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 20px' }}>
          {(['profile', 'password'] as Tab[]).map(t => (
            <button key={t} onClick={() => { setTab(t); setMsg(null) }}
              style={{ padding: '10px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${tab === t ? 'var(--accent, #c9a84c)' : 'transparent'}`, color: tab === t ? 'var(--accent, #c9a84c)' : 'rgba(255,255,255,0.45)', fontWeight: tab === t ? 700 : 400, fontSize: 13, cursor: 'pointer', textTransform: 'capitalize' as const }}>
              {t === 'password' ? 'Change Password' : 'Profile'}
            </button>
          ))}
        </div>

        <div style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '24px 0', fontSize: 13 }}>Loading…</div>
          ) : (
            <>
              {msg && (
                <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 16, background: msg.type === 'ok' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)', color: msg.type === 'ok' ? '#4ade80' : '#f87171', border: `1px solid ${msg.type === 'ok' ? 'rgba(74,222,128,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
                  {msg.text}
                </div>
              )}

              {tab === 'profile' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Read-only info */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, marginBottom: 4 }}>
                    {[
                      ['Call Sign', profile?.call_sign ?? session?.callSign],
                      ['Rank', profile?.rank ?? session?.rank],
                      ['Employee No.', profile?.employee_no || '—'],
                      ['Status', profile?.status ?? 'approved'],
                    ].map(([l, v]) => (
                      <div key={String(l)}>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' as const, letterSpacing: '.06em', marginBottom: 2 }}>{l}</div>
                        <div style={{ fontSize: 13, color: '#f1f5f9', fontWeight: 600 }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <label style={lbl}>Full name *</label>
                    <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="Last, First" />
                  </div>
                  <div>
                    <label style={lbl}>Email address</label>
                    <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>Used for password resets and notifications.</div>
                  </div>
                  <div>
                    <label style={lbl}>Phone number</label>
                    <input style={inp} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" />
                  </div>
                  <button
                    onClick={saveProfile} disabled={saving}
                    style={{ padding: '10px 0', background: saving ? 'rgba(255,255,255,0.1)' : 'var(--accent, #c9a84c)', color: '#1a2744', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}
                  >{saving ? 'Saving…' : 'Save Changes'}</button>
                </div>
              )}

              {tab === 'password' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={lbl}>Current password</label>
                    <input style={inp} type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Your current password" />
                  </div>
                  <div>
                    <label style={lbl}>New password</label>
                    <input style={inp} type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 8 characters" />
                  </div>
                  <div>
                    <label style={lbl}>Confirm new password</label>
                    <input style={inp} type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repeat new password" />
                  </div>
                  <button
                    onClick={changePassword} disabled={saving}
                    style={{ padding: '10px 0', background: saving ? 'rgba(255,255,255,0.1)' : 'var(--accent, #c9a84c)', color: '#1a2744', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}
                  >{saving ? 'Updating…' : 'Change Password'}</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
